import { useCallback } from 'react'
import { useAuth } from 'react-oidc-context'
import { useQueryClient } from '@tanstack/react-query'
import { clearAppSession } from './clearAppSession'
import { notifyLogout } from './logoutNotify'

/**
 * Çıkış yolundaki bir adımın en fazla bekletebileceği süre.
 *
 * <p>`notifyLogout`'un kendi zaman aşımıyla (`LOGOUT_NOTIFY_TIMEOUT_MS`) **aynı** değer; bilinçli
 * olarak ayrı bir sabit, çünkü ölçtüğü şey farklı: orada bir ağ isteği, burada yerel bir temizlik.
 */
export const LOGOUT_STEP_TIMEOUT_MS = 2000

/**
 * Bir adımı sınırlı süre bekler ve **asla reddetmez**. Süre dolarsa çağıran devam eder; adımın
 * kendisi arka planda sürmeye çalışır (sayfa yönlendirmesi onu zaten sonlandırır).
 */
function boundedStep(step: Promise<unknown>): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, LOGOUT_STEP_TIMEOUT_MS)
    step.then(
      () => {
        clearTimeout(timer)
        resolve()
      },
      () => {
        clearTimeout(timer)
        resolve()
      },
    )
  })
}

/**
 * Çıkış (FR-017): yerel oturum/önbellek temizliği + IdP RP-initiated logout (`end_session_endpoint`).
 * SSO oturumu da kapanır → sonraki giriş yeniden kimlik sorar (Session 2026-07-11). Çıkış sonrası dönüş
 * `post_logout_redirect_uri` (oidcSettings) iledir.
 *
 * <p>Temizlik artık {@link clearAppSession}'a devredilmiştir (062 FR-010a, research D7a): elle tutulan
 * `APP_SESSION_KEYS` listesi **kaldırıldı** — anahtarlar uygulama ön ekiyle taranarak silindiği için
 * kural kendiliğinden kapsayıcıdır. `removeOidcUser: false` bilinçlidir: OIDC kullanıcı kaydını
 * `signoutRedirect` kendisi siler ve silmeden önce `id_token`'a ihtiyacı vardır (`id_token_hint` → IdP'de
 * SSO oturumunun da kapanması); kaydı önceden silmek RP-initiated logout'u sessizce bozardı.
 *
 * <p>Temizlik başarısız olsa bile çıkış yönlendirmesi **yapılır** — kullanıcı çıkış isteğinde çıkmazda
 * bırakılmaz. **Asılı kalma da kapsanır** (094): temizlik yalnız {@link LOGOUT_STEP_TIMEOUT_MS} kadar
 * beklenir, sonra yönlendirmeye geçilir. Hata yutmak yetmiyordu — hiç çözülmeyen bir promise `catch`'e
 * de düşmez, `.finally`'yi de tetiklemez; o durumda kullanıcı **hiç** çıkamazdı.
 *
 * <p><b>Sunucuya bildirim (094):</b> temizlikten ve yönlendirmeden **önce** {@link notifyLogout}
 * çağrılır — belirteç o an hâlâ geçerlidir. Sunucu bu bildirimi almadan, çıkıştan önce kopyalanmış bir
 * belirteç süresi dolana kadar kabul edilmeye devam ediyordu (sızma testi bulgusu BGVL-137400).
 * Bildirim **beklenir** ama sonucu **umursanmaz**: `notifyLogout` hata fırlatmaz ve kendi zaman aşımı
 * vardır, dolayısıyla çıkış akışı her koşulda tamamlanır.
 */
export function useLogout() {
  const auth = useAuth()
  const queryClient = useQueryClient()
  return useCallback(() => {
    // Her adım `boundedStep` ile sarılır; `.finally()` zinciri kullanılmaz. İki ayrı arıza biçimi var
    // ve ikisi de kullanıcıyı çıkışsız bırakabilir:
    //   1) REDDETME — `p.finally(fn)` reddi İLETİR, zincirin sonunda sahipsiz bir reddedilmiş promise
    //      kalır. Vitest bunu "Unhandled Rejection" sayar (testler geçse bile süreç 1 ile çıkar, CI
    //      kırmızı — aynı tuzak `setup.ts`'te Quill için de not edilmiş); üretimde de konsola sebepsiz
    //      bir hata basar.
    //   2) ASILI KALMA — hiç çözülmeyen bir promise ne `catch`'e düşer ne `.finally`'yi tetikler.
    //      `clearAppSession` içindeki `queryClient.cancelQueries()` teoride burada takılabilir; o an
    //      `signoutRedirect` HİÇ çağrılmaz ve kullanıcı çıkamaz. Yalnız hata yutmak bunu kapatmıyordu.
    // Bedeli kabul edilmiştir: süre dolarsa yönlendirme temizlik bitmeden başlar ve birkaç `ceosuite:`
    // anahtarı geride kalabilir. Kullanıcının hiç çıkamamasından iyidir.
    void (async () => {
      await boundedStep(notifyLogout())
      await boundedStep(clearAppSession(queryClient, { removeOidcUser: false }))
      // D2: bu da sarılır — ama gerekçe ölçümle düzeltildi. Önce "reddedebilir (IdP metadata'sı
      // çekilemedi, `end_session_endpoint` yok)" yazıyordu; `react-oidc-context` kaynağına bakınca bu
      // YANLIŞ çıktı: kütüphane tüm navigator metotlarını (`signoutRedirect` dahil) kendi
      // `try/catch`'ine alıp hatayı `auth.error`'a çeviriyor ve `null` döndürüyor — yani o hatalar
      // buraya HİÇ gelmiyor. Reddetme yok; fırlatan tek yol `unsupportedEnvironment` stub'ı
      // (`window` yok: SSR, ya da fırlatan bir test mock'u) ve o **senkron** fırlatıyor.
      // Kritik ayrım: senkron throw'u `Promise.resolve(auth.signoutRedirect()).catch(...)` yazımı
      // YAKALAYAMAZ — hata argüman değerlendirilirken, `Promise.resolve` çağrılmadan ve `.catch`
      // bağlanmadan önce atılır. `try/await/catch` ise iki biçimi de kapsayan tek yazımdır.
      try {
        await auth.signoutRedirect()
      } catch {
        // Yutulur: aksi hâlde yukarıdaki 1. maddedeki sahipsiz-reddedilmiş-promise durumu oluşur.
      }
    })()
  }, [auth, queryClient])
}
